import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, parseApiError } from '../lib/api';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { ClanWorkbench } from '../components/ClanWorkbench';
import { isAdminRole } from '../lib/roles';
import { isOrgManagerOfAny, managedOrgIds } from '../lib/orgAccess';
import { useEffectiveMe } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import {
  Users,
  AlertCircle,
  Building2,
  Send,
} from 'lucide-react';

// ── Request to join an org (clan-leader only) ─────────────────────────────────

function JoinOrgForm({ clanName }: { clanName: string }) {
  // ALL hooks above any early return
  const qc = useQueryClient();
  const [orgInput, setOrgInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const joinMut = useMutation({
    mutationFn: () => api.clanRequestJoinOrg(clanName, orgInput.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
      setOrgInput('');
      setError('');
      setSuccess('Request sent — awaiting org approval.');
    },
    onError: (e: Error) => {
      setSuccess('');
      setError(parseApiError(e).message);
    },
  });

  const submit = () => {
    if (!orgInput.trim()) return;
    joinMut.mutate();
  };

  return (
    <Card className="!p-4">
      <div className="mb-3 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-ink-meta shrink-0" />
        <h3 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">
          Request to join an org
        </h3>
      </div>
      <p className="mb-3 text-xs text-ink-secondary">
        Enter the org slug or ID to send a join request. The org owner or admin will need to approve it.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Org slug or ID…"
          value={orgInput}
          onChange={e => {
            setOrgInput(e.target.value);
            setError('');
            setSuccess('');
          }}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          className="input flex-1 text-sm"
          disabled={joinMut.isPending}
        />
        <button
          onClick={submit}
          disabled={!orgInput.trim() || joinMut.isPending}
          className={cn('btn-primary flex items-center gap-1.5 disabled:opacity-50', MORPH)}
        >
          <Send className="h-3.5 w-3.5" strokeWidth={2.5} />
          {joinMut.isPending ? 'Sending…' : 'Request'}
        </button>
      </div>
      {error && (
        <div className="mt-2 flex items-start gap-2 text-xs text-red-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span>{error}</span>
        </div>
      )}
      {success && !error && (
        <p className="mt-2 text-xs text-emerald-400">{success}</p>
      )}
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

// -- Org invite row (clan leader accept / decline) --

function OrgInviteRow({ clanName, orgId, orgName }: { clanName: string; orgId: string; orgName: string }) {
  const qc = useQueryClient();
  const [inlineMsg, setInlineMsg] = useState('');

  const acceptMut = useMutation({
    mutationFn: () => api.acceptOrgInvite(clanName, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clanOrgInvites', clanName] });
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
      setInlineMsg('');
    },
    onError: (e: Error) => {
      if (e.message.includes('409') || e.message.toLowerCase().includes('already affiliated')) {
        setInlineMsg('Already affiliated with an org ? leave first before accepting.');
      } else {
        setInlineMsg(parseApiError(e).message);
      }
    },
  });

  const declineMut = useMutation({
    mutationFn: () => api.declineOrgInvite(clanName, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clanOrgInvites', clanName] });
      setInlineMsg('');
    },
    onError: (e: Error) => { setInlineMsg(parseApiError(e).message); },
  });

  const busy = acceptMut.isPending || declineMut.isPending;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-white">{orgName}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setInlineMsg(''); declineMut.mutate(); }}
            disabled={busy}
            className={cn('btn-ghost text-xs disabled:opacity-50', MORPH)}
          >
            {declineMut.isPending ? 'Declining...' : 'Decline'}
          </button>
          <button
            onClick={() => { setInlineMsg(''); acceptMut.mutate(); }}
            disabled={busy}
            className={cn('btn-primary text-xs disabled:opacity-50', MORPH)}
          >
            {acceptMut.isPending ? 'Accepting...' : 'Accept'}
          </button>
        </div>
      </div>
      {inlineMsg && (
        <div className="flex items-start gap-1.5 text-xs text-amber-400">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>{inlineMsg}</span>
        </div>
      )}
    </div>
  );
}

export function ClanManagement() {
  const [searchParams] = useSearchParams();
  const paramCompId = searchParams.get('compId') ?? '';

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const [selectedCompId, setSelectedCompId] = useState(paramCompId);
  // View-as-aware copy of `me` — every gate/scope decision below reads THIS,
  // not the raw query result. Identity no-op whenever no view-as is active
  // (see lib/viewAs.ts's useEffectiveMe) — real behaviour is unchanged.
  const effectiveMe = useEffectiveMe(me);

  // Pending org invites for this clan (leader-only, pre-fetched before early return
  // to satisfy the Rules of Hooks — enabled guard keeps it a no-op for non-leaders).
  const leaderClanName = me?.clanName ?? '';
  const isLeaderForQuery = effectiveMe?.role === 'leader';
  const { data: orgInvitesData } = useQuery({
    queryKey: ['clanOrgInvites', leaderClanName],
    queryFn: () => api.clanOrgInvites(leaderClanName),
    enabled: isLeaderForQuery && !!leaderClanName,
  });

  // Org-admin de-gate: an ORG_ADMIN/ORG_OWNER of at least one org gets in
  // too, scoped to their own org(s)' comps below (isOrgManagerOnly / compList).
  // Reads effectiveMe: viewing-as-member (no org sim) loses this; viewing-as
  // org-admin-of-X gains it, scoped to X only.
  const isOrgManager = isOrgManagerOfAny(effectiveMe);

  // Clan management de-gate (Wall #1, 2026-07-17): isAdminRole (founder/
  // admin) instead of canWrite (mirrors the backend's canManageClanCapability,
  // whose founder/admin short-circuit is ALSO isAdminRole).
  if (!me || (effectiveMe?.role !== 'leader' && !isAdminRole(effectiveMe?.role) && !isOrgManager)) {
    return <Card className="text-center"><p className="text-gray-400">Leader or admin access required.</p></Card>;
  }

  const isFounder = effectiveMe?.role === 'founder';
  const isLeaderOnly = effectiveMe?.role === 'leader';
  // isAdminRole (not canWrite) — see the entry-gate comment above.
  const isPlatformAdminPlus = isAdminRole(effectiveMe?.role);
  // A pure org manager (not also a platform founder/admin) — scoped to
  // their own org(s)' comps only, never the platform-wide "All clans" browse.
  const isOrgManagerOnly = isOrgManager && !isPlatformAdminPlus;
  const isAdminPlus = isPlatformAdminPlus || isOrgManagerOnly;

  const managedIds = managedOrgIds(effectiveMe);
  // Org-scoped comp list for a pure org manager — mirrors TournamentSetup's
  // own bareOrgId-normalized filter. Founder/platform-admin see every comp
  // (managedIds === 'all'), unchanged existing behaviour.
  const compListAll = comps ?? [];
  const compList = managedIds === 'all'
    ? compListAll
    : compListAll.filter(c => managedIds.includes((c.orgId ?? '').replace(/^ORG#/, '')));

  // Empty string = the "All clans (no competition filter)" option. Do NOT fall
  // back to the first comp here for founder/admin — that swallowed the
  // All-clans selection so the filter appeared to do nothing and out-of-comp
  // clans never showed. A pure org manager has NO "All clans" option at all
  // (see the selector below) — that path lists EVERY clan platform-wide
  // (api.admin.clans, founder/admin-only server-side), so an org
  // manager must always be pinned to one of their own org's comps.
  const activeCompId = isOrgManagerOnly
    ? (selectedCompId && compList.some(c => c.id === selectedCompId) ? selectedCompId : (compList[0]?.id ?? ''))
    : selectedCompId;

  return (
    <div>
      <div className={cn('mb-8 flex items-center justify-between gap-3', SETTLE)} style={settleDelay(0)}>
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-ink-secondary" />
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isLeaderOnly ? 'My Clan' : 'Clan Management'}
            </h1>
            <p className="text-xs text-ink-meta">
              {isLeaderOnly
                ? 'Your clan roster and seats'
                : 'Create clans, manage seats, enroll in competitions'}
            </p>
          </div>
        </div>
        {/* Create Team removed (Lisa, 2026-07-07): teams live INSIDE a clan —
            create the clan first, then build teams on the clan's own board. */}
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <HelperCard
          id="clan-management-intro"
          title="How seats work"
          intro="Clans hold seats. Seats start as ghosts — placeholders waiting for a player. Players claim seats by matching their gamertag via the Discord bot or the portal hub."
          points={[
            { role: 'admin', text: 'Create clans, bulk-import ghost seats, enroll clans in competitions.' },
            { role: 'founder', text: 'Force-claim and swap seats for testing. Use the red debug buttons.' },
            { role: 'leader', text: 'Clan leaders run their own roster from the Leader Panel — not here.' },
          ]}
          className="mb-6"
        />
      </div>

      {/* Comp selector */}
      <div className={cn('mb-6', SETTLE)} style={settleDelay(2)}>
        <label className="label mb-1">Competition</label>
        <select
          value={activeCompId}
          onChange={e => setSelectedCompId(e.target.value)}
          className="input w-full max-w-md"
        >
          {/* "All clans" browses EVERY clan platform-wide (api.admin.clans,
              founder/admin-only server-side) — never offered to a
              pure org manager, who must stay pinned to their own org's comps. */}
          {!isOrgManagerOnly && <option value="">All clans (no competition filter)</option>}
          {isOrgManagerOnly && compList.length === 0 && <option value="">No competitions in your org(s) yet</option>}
          {compList.map(c => (
            <option key={c.id} value={c.id}>
              {c.name || c.id.replace('COMP#', '')} ({c.status})
            </option>
          ))}
        </select>
      </div>

      <div className={SETTLE} style={settleDelay(3)}>
        <ClanWorkbench
          compId={activeCompId}
          isFounder={isFounder}
          isAdminPlus={isAdminPlus}
          // Clan display-name preservation (Lisa, 2026-07-13): me.clanName is
          // always the canonical bare-slug identity (getRole()'s derived
          // form) — rebuild it into the full canonical CLAN# id so
          // ClanWorkbench's restrictToClan filter (now comparing against
          // the enrollment row's canonical clanId, since its `name` may be
          // a display name with special characters) matches correctly.
          restrictToClan={isLeaderOnly && me.clanName ? `CLAN#${me.clanName}` : undefined}
        />
      </div>

      {/* ── Clan leader: request to join an org ── */}
      {isLeaderOnly && me.clanName && (
        <div className={cn('mt-6', SETTLE)} style={settleDelay(4)}>
          <JoinOrgForm clanName={me.clanName} />
        </div>
      )}

      {/* Clan leader: pending org invitations */}
      {isLeaderOnly && me.clanName && (orgInvitesData?.invites?.length ?? 0) > 0 && (
        <div className={cn('mt-4', SETTLE)} style={settleDelay(5)}>
          <Card className="!p-4">
            <div className="mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-forge shrink-0" />
              <h3 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">
                Org invitations
              </h3>
            </div>
            <p className="mb-3 text-xs text-ink-secondary">
              These organisations have invited your clan to join. Accept or decline each one.
            </p>
            <div className="space-y-3 divide-y divide-white/5">
              {orgInvitesData!.invites.map(invite => (
                <div key={invite.orgId} className="pt-3 first:pt-0">
                  <OrgInviteRow
                    clanName={me.clanName!}
                    orgId={invite.orgId}
                    orgName={invite.orgName}
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

    </div>
  );
}
